import { resolveStoredFileUrl } from "utils/storedFileUrl";
// React
import PropTypes from "prop-types";
import { Fragment, useCallback, useContext, useEffect, useRef, useState } from "react";

// @mui material components
import {
  Autocomplete,
  Button,
  Card,
  Checkbox,
  Divider,
  Fade,
  Grid,
  Icon,
  Input,
  InputLabel,
  Modal,
  TextField,
  Tooltip,
} from "@mui/material";

// Custom UI components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import { MessageManager } from "context";
import { UserContext } from "context/user";
import { PERMISOS_COMPRAS, tienePermisoCompras } from "layouts/compras/permisos";
import { ConfirmarContext } from "context/ConfirmarContext";
import SoftInput from "components/SoftInput";
import DataTable from "components/Tables/Table-v2";
import { formatMoneyPunto } from "utils/formatters";
import SinDatos from "components/Carteles/SinDatos";
import SoftButton from "components/SoftButton";
import { getUproColor } from "utils/colors";
import SoftInputNumber from "components/SoftInputNumberUpgrade";
import { gql, useMutation, useQuery } from "@apollo/client";
import { PASOS } from "../pasosCircuito";
import { mensajeError } from "layouts/compras/mensajeError";
import { nombreCompraDetalle, unidadCompraDetalle } from "utils/presentacionCompra";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "90%",
  width: { xs: "90%", sm: "80%", md: "80%", lg: "70%", xl: "60%", xxl: "50%" },
  overflow: "auto",
  p: 3,
};
const GET_PEDIDO_PRESUPUESTOS = gql`
  query getPedidoPresupuestos($id: ID!) {
    pedidosCompra(filter: { id: $id }) {
      id
      estados {
        id
        base {
          nombre
        }
        templateData
        file
      }
    }
  }
`;
// Concurso: los renglones desiertos pueden ordenarse a cualquier proveedor habilitado
const GET_PROVEEDORES_HABILITADOS = gql`
  query getProveedoresOrdenDirecta {
    proveedores(order: { field: "razonSocial", order: ASC }) {
      id
      razonSocial
      nombre
      apellido
      documento
      habilitado
    }
  }
`;
const allowedFileTypes = ".doc,.docx,.pdf";

export default function ModalNuevaProvision({
  open,
  handleClose,
  productosSinProvision,
  refetchParent,
  idPedido,
  tipoCompra,
}) {
  const esConcurso = tipoCompra === "Concurso";
  const { handleSnackbar } = useContext(MessageManager);
  const { user, verificarAcceso } = useContext(UserContext);
  const puedeCrear = tienePermisoCompras(user, verificarAcceso, PERMISOS_COMPRAS.CREAR_ORDEN_DIRECTA);
  const { data: dataProveedores } = useQuery(GET_PROVEEDORES_HABILITADOS, { skip: !open || !esConcurso });
  const proveedoresHabilitados = (dataProveedores?.proveedores || []).filter((p) => p.habilitado);
  const { confirmar } = useContext(ConfirmarContext);

  const [saveProvision, { loading: loadingMutation }] = useMutation(
    gql`
      mutation saveProvisionModalNuevaProvision($input: ProvisionInput!) {
        saveProvision(input: $input) {
          id
        }
      }
    `,
    {
      onCompleted: () => {
        handleSnackbar("Orden de provisión guardada", "success");
        refetchParent();
        handleClose();
      },
      onError: (error) => {
        handleSnackbar(mensajeError(error, "No se pudo guardar la orden de provisión"), "error");
      },
    }
  );

  const [productosElegidos, setProductosElegidos] = useState([]);
  const [proveedor, setProveedor] = useState(null);
  const [archivosPresupuesto, setArchivosPresupuesto] = useState([]);
  const [keyFile, setKeyFile] = useState(0);
  const [proveedoresConPresupuesto, setProveedoresConPresupuesto] = useState([]);
  const [presupuestosData, setPresupuestosData] = useState([]); // Para almacenar la relación proveedor-archivos

  useQuery(GET_PEDIDO_PRESUPUESTOS, {
    variables: { id: idPedido },
    skip: !idPedido || !open,
    onCompleted: (data) => {
      if (data?.pedidosCompra?.[0]?.estados) {
        // Extraer proveedores y archivos de los estados de "CARGA DE PRESUPUESTO"
        const estadosPresupuesto = data.pedidosCompra[0].estados.filter(
          estado => estado.base.nombre === PASOS.CARGA_PRESUPUESTO
        );

        const presupuestos = estadosPresupuesto
          .map(estado => ({
            proveedor: estado.templateData?.proveedor,
            archivos: estado.file || []
          }))
          .filter(item => item.proveedor && item.proveedor.id);

        const proveedores = presupuestos.map(item => item.proveedor);

        setProveedoresConPresupuesto(proveedores);
        setPresupuestosData(presupuestos);
      }
    }
  });

  useEffect(() => {
    if (open) {
      setProductosElegidos(
        productosSinProvision.map((item) => ({
          ...item,
          cantidadVerificada:
            item.cantidadPendienteProvision ?? item.cantidadVerificada,
          elegido: true,
          precioUnitario: item.precioEstimado || null,
        }))
      );
      // Limpiar proveedor seleccionado al abrir el modal
      setProveedor(null);
      setArchivosPresupuesto([]);
    }
  }, [productosSinProvision, open]);

  // Actualizar archivos cuando se selecciona un proveedor
  useEffect(() => {
    if (proveedor && presupuestosData.length > 0) {
      const presupuestoData = presupuestosData.find(
        item => item.proveedor.id === proveedor.id
      );
      setArchivosPresupuesto(presupuestoData?.archivos || []);
    } else {
      setArchivosPresupuesto([]);
    }
  }, [proveedor, presupuestosData]);

  const clear = useCallback(() => {
    setProductosElegidos((prev) =>
      prev.map((item) => ({
        ...item,
        elegido: false,
        precioUnitario: null,
      }))
    );
  }, [setProductosElegidos]);

  const validate = () => {
    if (!proveedor || !idPedido) {
      return false;
    }
    if (productosElegidos.filter((item) => item.elegido).length === 0) {
      return false;
    }

    // Cada renglón elegido necesita un precio mayor a cero (el backend valida lo mismo)
    if (productosElegidos.some((item) => item.elegido && !(Number(item.precioUnitario) > 0))) {
      return false;
    }

    return true;
  };

  const handleSave = () => {
    let input = {};

    input.idPedidoCompra = idPedido;
    input.idProveedor = proveedor?.id;
    // No enviamos archivos ya que están asociados al presupuesto en el tab2
    input.detalles = productosElegidos
      .filter((i) => i.elegido)
      .map((item) => ({
        idMateriaPrima: item.materiaPrima?.id || null,
        idPedidoCompraDetalle: item.id,
        cantidad: parseFloat(item.cantidadVerificada ?? item.cantidad) || 0,
        precioUnitario: item.precioUnitario,
      }));

    saveProvision({ variables: { input } });
  };

  const subtotal = productosElegidos
    .filter((item) => item.elegido)
    .reduce((sum, item) => {
      const precio = parseFloat(item.precioUnitario) || 0;
      const cantidad = parseFloat(item.cantidadVerificada ?? item.cantidad) || 0;
      return sum + precio * cantidad;
    }, 0);

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      onClose={handleClose}
      closeAfterTransition
    >
      <Fade in={open}>
        <Card sx={style}>
          <SoftBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ zIndex: 99 }}
          >
            <SoftBox display="flex" gap={1}>
              <Icon fontSize="small">info</Icon>
              <SoftTypography variant="h6">Nueva órden de provisión</SoftTypography>
            </SoftBox>
            <SoftBox display="flex" justifyContent="end" alignItems="center" gap={1}>
              <Tooltip title="Cerrar" placement="top">
                <Icon fontSize="medium" onClick={handleClose} sx={{ cursor: "pointer" }}>
                  close
                </Icon>
              </Tooltip>
            </SoftBox>
          </SoftBox>
          <Grid container display="flex" py={2} spacing={2}>
            <Grid item xs={6}>
              <InputLabel htmlFor="proveedor">
                <SoftTypography component="label" variant="caption" fontWeight="bold">
                  Proveedor
                  <SoftTypography variant="caption" fontWeight="bold" color="primary">
                    &nbsp;*
                  </SoftTypography>
                </SoftTypography>
              </InputLabel>
              <Autocomplete
                options={esConcurso ? proveedoresHabilitados : proveedoresConPresupuesto || []}
                getOptionLabel={(proveedor) =>
                  [
                    proveedor.razonSocial || proveedor.nombre,
                    proveedor.apellido,
                    ` - ${proveedor.documento}`,
                  ].filter(Boolean).join(" ")
                }
                onChange={(e, newValue) => {
                  setProveedor(newValue);
                }}
                renderInput={(params) => (
                  <TextField {...params} variant="outlined" placeholder="Buscar proveedor..." />
                )}
                noOptionsText={esConcurso ? "No hay proveedores habilitados" : "No hay proveedores con presupuesto cargado"}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <SoftTypography variant="caption" fontWeight="bold" mb={1}>
                Archivos de Presupuesto
              </SoftTypography>
              {archivosPresupuesto.length > 0 ? (
                <SoftBox>
                  {archivosPresupuesto.map((archivo, index) => {
                    // Extraer solo el nombre del archivo de la ruta
                    const nombreArchivo = archivo.split('/').pop().split('\\').pop();
                    const nombreLimpio = nombreArchivo.replace(/^public[\\\/]pedidos[\\\/]/, '');

                    return (
                      <SoftBox
                        key={index}
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        my={1}
                        p={1}
                        borderRadius="md"
                        sx={{ outline: "1px solid #e0e0e0" }}
                      >
                        <Tooltip title={nombreLimpio} placement="top">
                          <SoftTypography variant="caption" sx={{ cursor: "help" }}>
                            {nombreLimpio.length > 30
                              ? nombreLimpio.substring(0, 30) + '...'
                              : nombreLimpio}
                          </SoftTypography>
                        </Tooltip>
                        <Tooltip title="Ver archivo" placement="top">
                          <Icon
                            sx={{ cursor: "pointer" }}
                            onClick={() => {
                              window.open(resolveStoredFileUrl(archivo), '_blank');
                            }}
                          >
                            launch
                          </Icon>
                        </Tooltip>
                      </SoftBox>
                    );
                  })}
                </SoftBox>
              ) : (
                <SoftBox
                  p={2}
                  textAlign="center"
                  sx={{
                    border: "1px dashed #ccc",
                    borderRadius: "8px",
                    backgroundColor: "#f9f9f9"
                  }}
                >
                  <SoftTypography variant="caption" color="secondary">
                    {proveedor
                      ? "No hay archivos de presupuesto para este proveedor"
                      : "Seleccione un proveedor para ver sus archivos de presupuesto"
                    }
                  </SoftTypography>
                </SoftBox>
              )}
            </Grid>
          </Grid>
          {productosElegidos?.length > 0 ? (
            <Grid container>
              <Grid item xs={0.5}></Grid>
              <Grid item xs={1} textAlign="center">
                <SoftTypography variant="caption" fontWeight="bold">
                  Código
                </SoftTypography>
              </Grid>
              <Grid item xs={3.5}>
                <SoftTypography variant="caption" fontWeight="bold">
                  Nombre producto
                </SoftTypography>
              </Grid>
              <Grid item xs={2} textAlign="center">
                <SoftTypography variant="caption" fontWeight="bold">
                  Pack
                </SoftTypography>
              </Grid>
              <Grid item xs={1} textAlign="center">
                <SoftTypography variant="caption" fontWeight="bold">
                  Cantidad
                </SoftTypography>
              </Grid>
              <Grid item xs={1.5} textAlign="center">
                <SoftTypography variant="caption" fontWeight="bold">
                  Unidad
                </SoftTypography>
              </Grid>
              <Grid item xs={1.5} textAlign="center">
                <SoftTypography variant="caption" fontWeight="bold">
                  Precio Unitario
                </SoftTypography>
              </Grid>
              <Grid item xs={1} textAlign="center">
                <SoftTypography variant="caption" fontWeight="bold">
                  Elegir
                </SoftTypography>
              </Grid>
              <Grid container>
                {productosElegidos?.map((item, index) => (
                  <Fragment key={index}>
                    <Grid item xs={0.5} display="flex" alignItems="center" justifyContent="center">
                      <Icon
                        sx={{
                          color:
                            item.materiaPrima?.tipo === "Material"
                              ? getUproColor("uproYellow")
                              : item.materiaPrima?.tipo === "Consumible"
                                ? getUproColor("uproGreen")
                                : getUproColor("uproPink"),
                        }}
                        fontSize="small"
                      >
                        {item.materiaPrima?.tipo === "Material"
                          ? "category"
                          : item.materiaPrima?.tipo === "Consumible"
                            ? "recycling"
                            : "layers"}
                      </Icon>
                    </Grid>
                    <Grid item xs={1} textAlign="center" alignContent="center">
                      <SoftTypography variant="caption">{item.id}</SoftTypography>
                    </Grid>
                    <Grid item xs={3.5}>
                      <SoftTypography variant="caption" alignContent="center">
                        {nombreCompraDetalle(item)}
                      </SoftTypography>
                    </Grid>
                    <Grid item xs={2} textAlign="center" alignContent="center">
                      <SoftTypography variant="caption">{item.requierePack ? "SI" : "NO"}</SoftTypography>
                    </Grid>
                    <Grid item xs={1} textAlign="center" alignContent="center">
                      <Tooltip title={"1 " + (item?.unidadPack || "") + " = " + item?.equivalenciaPack?.materiaPrima + " " + (item.materiaPrima?.tipoUnidad?.abreviatura || "")} placement="top">
                        <SoftTypography variant="caption">{item.cantidadVerificada}</SoftTypography>
                      </Tooltip>
                    </Grid>
                    <Grid item xs={1.5} textAlign="center" alignContent="center">
                      <SoftTypography variant="caption">
                        {unidadCompraDetalle(item)}
                      </SoftTypography>
                    </Grid>
                    <Grid item xs={1.5} textAlign="center" alignContent="center">
                      <SoftInputNumber
                        value={item.precioUnitario}
                        disabled={!item.elegido}
                        onChange={(e) => {
                          setProductosElegidos((prev) => {
                            const newProductos = [...prev];
                            newProductos[index] = {
                              ...newProductos[index],
                              // Campo vacío = sin precio (evita NaN)
                              precioUnitario: Number.isFinite(parseFloat(e.target.value))
                                ? parseFloat(e.target.value)
                                : null,
                            };
                            return newProductos;
                          });
                        }}
                      />
                    </Grid>
                    <Grid item xs={1} textAlign="center" alignContent="center">
                      <Checkbox
                        checked={item.elegido}
                        onChange={(e) => {
                          setProductosElegidos((prev) => {
                            const newProductos = [...prev];
                            newProductos[index] = {
                              ...newProductos[index],
                              elegido: e.target.checked,
                            };
                            return newProductos;
                          });
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Divider
                        orientation="horizontal"
                        flexItem
                        sx={{
                          height: 0.3,
                          margin: "2px 0",
                        }}
                      />
                    </Grid>
                  </Fragment>
                ))}
              </Grid>
            </Grid>
          ) : (
            <SinDatos />
          )}
          <SoftBox display="flex" justifyContent="space-between" alignItems="center" p={1} gap={2}>
            <Tooltip title="Limpiar selección e importes" placement="top">
              <SoftButton color="uproYellow" size="small" circular onClick={clear}>
                Limpiar
              </SoftButton>
            </Tooltip>
            <SoftBox display="flex" gap={2}>
              <SoftTypography variant="body2" fontWeight="bold">
                Total:
              </SoftTypography>
              <SoftTypography variant="body2" fontWeight="bold" color="dark">
                ${formatMoneyPunto(subtotal)}
              </SoftTypography>
            </SoftBox>
          </SoftBox>
          <SoftBox display="flex" justifyContent="flex-end" mt={2}>
            <SoftButton
              variant="contained"
              color="uproGreen"
              circular
              disabled={!puedeCrear || loadingMutation || !validate()}
              onClick={() => {
                confirmar({
                  title: "Confirmar",
                  message: "¿Desea confirmar la orden provisión?",
                  func: handleSave,
                });
              }}
            >
              Confirmar
            </SoftButton>
          </SoftBox>
        </Card>
      </Fade>
    </Modal>
  );
}

ModalNuevaProvision.defaultProps = {
  refetchParent: () => { },
};

ModalNuevaProvision.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  productosSinProvision: PropTypes.array,
  refetchParent: PropTypes.func,
  idPedido: PropTypes.string,
  tipoCompra: PropTypes.string,
};
